// This constants class is for the prefix for the files that

import { AllCaseAttachmentsModel } from "app/models/all-case-attachments.model";

// are renamed based on what type of upload they are.
export class DocumentConstants {
    public static ABSENCE_OF_CONFLICT: string = `absence-of-conflict`;
    public static ADDITIONAL_DOCUMENTATION: string = `additional-documentation`;
    public static ALL_PEGYLATED_SUBSTANCES: string = `all-pegylated-substances`;
    public static ANTIBODY_DRUG_CONJUGATES: string = `antibody-drug-conjugates`;
    public static CAS_REGISTRY_INFO: string = `cas-registry-info`;
    public static CELL_THERAPIES: string = `cell-therapies`;
    public static COVER_LETTER: string = `cover-letter`;
    public static GENE_THERAPIES: string = `gene-therapies`;
    public static MOLECULAR_FORMULA: string = `molecular-formula`;
    public static MOLECULAR_FORMULA_FOR_SALT: string = `molecular-formula-for-salt`;
    public static MOLECULAR_FORMULA_SINGLE_ENTITY: string = `molecular-formula-single-entity`;
    public static MONOMER_AND_POLYMER_FORMULAS: string = `monomer-and-polymer-formulas`;
    public static NUCLEIC_ACID_SALTS: string = `nucleic-acid-salts`;
    public static PEGYLATED_NUCLEIC_ACID_BASED_SUBSTANCES: string = 'pegylated-nucleic-acid-based-substances';
    public static PEGYLATED_PROTEINS_AND_PEPTIDES: string = 'pegylated-proteins-and-peptides';
    public static PROOF_OF_PAYMENT: string = `proof-of-payment`;
    public static SEQUENCE_AND_STRUCTURAL_INFO = `sequence-and-structural-info`;
    public static SEQUENCE_AND_STRUCTURAL_INFO_ANTIBODIES = `sequence-antibodies`;
    public static SEQUENCE_AND_STRUCTURAL_INFO_NUCLEIC_ACID = `sequence-nucleic-acid`;
    public static STATEMENT_OF_ADOPTION: string = `statement-of-adoption`;
    public static STRUCTURAL_FORMULA_FOR_NONE: string = `structural-formula`;
    public static STRUCTURAL_FORMULA_FOR_SALT: string = `structural-formula-for-salt`;
    public static STRUCTURAL_FORMULA_SINGLE_ENTITY: string = `structural-formula-single-entity`;
    public static TRADEMARK_SEARCH_RESULTS: string = `trademark-search-results`;
}

export class FormRequiredDocumentsMapping {
    public static FORM_A_REQUIRED_DOC_NAMES: string[] = [DocumentConstants.STRUCTURAL_FORMULA_SINGLE_ENTITY, DocumentConstants.STRUCTURAL_FORMULA_FOR_SALT, DocumentConstants.CAS_REGISTRY_INFO, DocumentConstants.ABSENCE_OF_CONFLICT];
    public static FORM_B_REQUIRED_DOC_NAMES: string[] = [DocumentConstants.STRUCTURAL_FORMULA_SINGLE_ENTITY, DocumentConstants.CAS_REGISTRY_INFO, DocumentConstants.ABSENCE_OF_CONFLICT];
    public static FORM_C_REQUIRED_DOC_NAMES: string[] = [DocumentConstants.STRUCTURAL_FORMULA_SINGLE_ENTITY, DocumentConstants.CAS_REGISTRY_INFO];
    public static FORM_D_REQUIRED_DOC_NAMES: string[] = [DocumentConstants.STRUCTURAL_FORMULA_SINGLE_ENTITY, DocumentConstants.CAS_REGISTRY_INFO];
    public static FORM_E_REQUIRED_DOC_NAMES: string[] = [DocumentConstants.STRUCTURAL_FORMULA_SINGLE_ENTITY, DocumentConstants.CAS_REGISTRY_INFO, DocumentConstants.ABSENCE_OF_CONFLICT];
    public static FORM_F_REQUIRED_DOC_NAMES: string[] = [DocumentConstants.ABSENCE_OF_CONFLICT];

    public static REQUIRED_DOCS_MAP: Map<string, string[]> = new Map([
        ['FormA', this.FORM_A_REQUIRED_DOC_NAMES],
        ['FormB', this.FORM_B_REQUIRED_DOC_NAMES],
        ['FormC', this.FORM_C_REQUIRED_DOC_NAMES],
        ['FormD', this.FORM_D_REQUIRED_DOC_NAMES],
        ['FormE', this.FORM_E_REQUIRED_DOC_NAMES],
        ['FormF', this.FORM_F_REQUIRED_DOC_NAMES]
    ]);

    public static hasAllRequiredDocumentsAttached(formType: string, attachmentsModel: AllCaseAttachmentsModel): boolean {
        if(!formType || !attachmentsModel) {
            return false;
        }

        const requiredNames = this.REQUIRED_DOCS_MAP.get(formType);
        
        if(!requiredNames || requiredNames.length < 1) {
            return true;
        }

        for(let name of requiredNames) {
            if(!attachmentsModel.hasAttachmentsForSection(name)) {
                return false;
            }
        }

        return true;
    }

    public static isDocumentRequiredForForm(formType: string, documentType: string): boolean {
        if(!formType || !documentType) {
            return false;
        }

        const requiredNames = this.REQUIRED_DOCS_MAP.get(formType);
        if(!requiredNames) {
            return false;
        }

        return requiredNames.includes(documentType);
    }

} 

export class DocumentExtensionHandling {
    public static EXCLUDED_FILE_EXTENSIONS: string[] = [`exe`, `msi`, `com`, `bat`, `cmd`, `dll`, `scr`, `pif`, `vbs`, `app`, `command`, `sh`, `bin`, `out`, `py`, `jar`];
    public static CHEMDRAW_FILE_EXTENSIONS: string[] = [`cdx`, `cdxml`];
    public static IMAGE_FILE_EXTENSIONS: string[] = [`bmp`, `gif`, `jpeg`, `jpg`, `jfif`, `pjpeg`, `pjp`, `png`, `svg`, `tif`, `tiff`, `mol`];
    public static MS_EXCEL_FILE_EXTENSIONS: string[] = [`xls`, `xlsx`];
    public static MS_WORD_FILE_EXTENSIONS: string[] = [`doc`, `docx`];
    public static CSV_FILE_EXTENSIONS: string[] = [`csv`];
    public static PDF_FILE_EXTENSIONS: string[] = [`pdf`];

    public static CHEMDRAW_ACCEPT_TYPE: string[] = [`.cdx`, `.cdxml`];
    public static IMAGE_ACCEPT_TYPE: string[] = [`image/bmp`, `image/gif`, `image/jpeg`, `image/jpg`, `image/png`, `image/svg+xml`, `image/tif`, `image/tiff`, `image/mol`];
    public static MS_EXCEL_ACCEPT_TYPE: string[] = [`.xls`, `.xlsx`];
    public static MS_WORD_ACCEPT_TYPE: string[] = [`.doc`, `.docx`];
    public static CSV_ACCEPT_TYPE: string[] = [`.csv`];
    public static PDF_ACCEPT_TYPE: string[] = [`.pdf`];

    public static extractFileExtension(fileName: string): string {
        const extnIdx = fileName.lastIndexOf('.') + 1; // So we skip the dot.
        if(extnIdx === 0) {
            return '';
        }
        return fileName.slice(extnIdx).toLowerCase();
    }

    public static allAcceptableUploadTypes(): string[] {
        return [
            ...DocumentExtensionHandling.CHEMDRAW_ACCEPT_TYPE, ...DocumentExtensionHandling.IMAGE_ACCEPT_TYPE, ...DocumentExtensionHandling.MS_EXCEL_ACCEPT_TYPE,
            ...DocumentExtensionHandling.MS_WORD_ACCEPT_TYPE, ...DocumentExtensionHandling.CSV_ACCEPT_TYPE, ...DocumentExtensionHandling.PDF_ACCEPT_TYPE
        ];
    }

    public static isFileTypeAllowedForUpload(fileName: string, allowedFileTypes?: string[]): boolean {
        let fileExtension = this.extractFileExtension(fileName);
        
        if(!fileExtension) {
            return false;
        }

        // If we require specific file types, make sure the file is of the correct type.
        if(allowedFileTypes && allowedFileTypes.length > 0 && !allowedFileTypes.includes(fileExtension)) {
            return false;
        }

        return !DocumentExtensionHandling.EXCLUDED_FILE_EXTENSIONS.includes(fileExtension);
    }

    public static fileTypeArrayAsString(fileTypes: string[]): string {
        if(!fileTypes) {
            return '';
        }

        let fileExtnsStr = '';
        let lastIndex = fileTypes.length - 1;

        fileTypes.forEach((type, idx) => {
            fileExtnsStr += type;
            if(idx < lastIndex) {
                fileExtnsStr += ', ';
            }
        });
        
        return fileExtnsStr;
    }

}